'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { NumberTicker } from '@/components/ui/number-ticker';
import { LIGHT_TOKENS } from './light-tokens';
import { PAPER_INK } from '../ui/paper-tokens';
import type { Locale } from '../hooks/useLocale';

export type BigStatProps = {
  value: number;
  decimalPlaces?: number;
  suffix?: string;
  label: string;
  /**
   * [MINOR fix, r4-p3-review-plan.md]: the visible number and the adjacent
   * `sr-only` announcement (`SocialProofSection.tsx`'s `formatMetricCount`/
   * `formatMetricRate`) must agree on digit-grouping for the SAME value — a
   * `ro`-locale visitor previously saw en-US grouping ("1,234") here while a
   * screen reader announced ro grouping ("1.234") for the identical number.
   * Defaults to `'en'` (matches the prior hardcoded en-US behavior when a
   * caller doesn't pass one — e.g. gallery/unit-test usage outside a
   * localized page).
   */
  locale?: Locale;
  /** `'reduce'` forces the static branch deterministically (gallery/test
   * coverage, same contract as `ui/StatTile.tsx`'s prop); `'system'`
   * (default) reads `matchMedia` post-hydration. */
  motionPreference?: 'system' | 'reduce';
  /**
   * R5-P3 post-review fix (SERIOUS finding, [ADV-18]): additive pass-through
   * to the inner `NumberTicker`'s own `startSignal` prop — `undefined`
   * (default) preserves the ORIGINAL self-contained behavior byte-for-byte
   * (each ticker still self-observes its own viewport entry via
   * `useInView({ once: true })`). A caller that orchestrates several
   * `BigStat`s together (`SocialProofSection.tsx`'s stats-row dwell trigger,
   * `social-proof/BigStatsMotion.tsx`'s `onStatsStart`) passes an explicit
   * `true`/`false` so all three tick together only once comfortably in
   * view, instead of each one firing independently "at the band's first
   * pixel" (the R4 kb "serial timers finish before attention arrives"
   * class this seam exists to close). See `NumberTicker`'s own doc comment
   * for the full `??` (never `||`) semantics this forwards unchanged.
   */
  startSignal?: boolean;
  /**
   * 2026-07-17 R7 integration seam (design contract L3.1 / brief §2f,
   * "MUST-FIX"): additive, default `'light'` (byte-identical to every
   * existing consumer, incl. the dev gallery). `surface="paper"` re-inks
   * the left-rule + digit + label to the warm `PAPER_INK` family —
   * `LightBand`'s own doc comment has the full cross-lane seam rationale.
   * The rule's FILL has no `bg` recipe in `PaperInkRecipe` (unlike
   * `LIGHT_TOKENS.statRuleBg`, a dedicated fill token) — consumed via
   * inline `style` sourcing `PAPER_INK.primary.hex`, the type's own
   * documented escape hatch, same pattern `SocialProofSection.tsx`'s
   * `UnavailableStat` twin already uses for the identical gap.
   */
  surface?: 'light' | 'paper';
  className?: string;
};

const BIG_STAT_VALUE_CLASS =
  'text-[clamp(36px,3.6vw,52px)] font-[590] leading-none tracking-[-0.02em] ' +
  '[font-variant-numeric:lining-nums_tabular-nums_slashed-zero]';

/**
 * [MINOR fix, r4-p3-review-plan.md]: non-Intl, hand-rolled grouping — same
 * shape as `social-proof/formatters.ts`'s `formatMetricCount`/
 * `formatMetricRate` (that file's own doc comment: "Do not call
 * Intl.NumberFormat() ... in the initial render ... kept non-Intl everywhere
 * so a mid-session locale toggle can never diverge"). Previously this used
 * `Intl.NumberFormat('en-US', ...)` unconditionally, which is exactly the
 * source of the en-US-vs-ro grouping mismatch this fix closes.
 */
function formatBigStatValue(
  value: number,
  decimalPlaces: number,
  suffix: string | undefined,
  locale: Locale
): string {
  const fixed = value.toFixed(decimalPlaces);
  const [intPartRaw, decPart] = fixed.split('.');
  const negative = intPartRaw.startsWith('-');
  const digits = negative ? intPartRaw.slice(1) : intPartRaw;
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(digits.slice(Math.max(0, end - 3), end));
  }
  const thousandsSep = locale === 'ro' ? '.' : ',';
  const decimalSep = locale === 'ro' ? ',' : '.';
  const grouped = (negative ? '-' : '') + groups.join(thousandsSep);
  const formatted = decPart !== undefined ? `${grouped}${decimalSep}${decPart}` : grouped;
  return suffix ? `${formatted}${suffix}` : formatted;
}

/**
 * BigStat — the light-band KPI primitive (masterplan §1.2 "Big-stat
 * left-rule" + §1.3 [AMENDED per D4] export pin: named client export with
 * `motionPreference`): a 2px ink left-rule at ~60% block height + an ink
 * `NumberTicker` + a muted mono label — the attio `t1N69kp03G` grammar
 * recast in ink-on-paper.
 *
 * The ~60% rule height is pure CSS, no measurement: the outer row is
 * `items-stretch` (default flex behavior), so the rule's `flex items-center`
 * wrapper inherits 100% of the row's height (driven by the number+label
 * column, the tallest sibling); the rule itself is then `height:'60%'`
 * *of that already-stretched wrapper* — a genuine CSS percentage, not a
 * magic px guess.
 *
 * SSR-safe PRM guard is the SAME SHAPE as `ui/StatTile.tsx`'s (build brief
 * §6.3 [AMENDED b8]: server + first hydrated render are ALWAYS static;
 * `NumberTicker` mounts only once an effect confirms `prefers-reduced-
 * motion` is off) — reimplemented here as its OWN file rather than an edit
 * to `StatTile.tsx`, because P2 `ui/**` stays frozen (masterplan Law #12 /
 * [AMENDED per B1]: light needs are met by this NEW `ui-light/**` layer,
 * never by editing a frozen P2 primitive).
 */
export function BigStat({
  value,
  decimalPlaces = 0,
  suffix,
  label,
  locale = 'en',
  motionPreference = 'system',
  startSignal,
  surface = 'light',
  className,
}: BigStatProps) {
  const [systemReduced, setSystemReduced] = useState(true);
  const paper = surface === 'paper';

  useEffect(() => {
    if (motionPreference !== 'system') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setSystemReduced(mql.matches);
    const onChange = (event: MediaQueryListEvent) => setSystemReduced(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [motionPreference]);

  const reduced = motionPreference === 'reduce' ? true : systemReduced;

  return (
    <div
      data-primitive="BigStat"
      data-surface={surface}
      className={cn('flex items-stretch gap-3', className)}
    >
      <div aria-hidden="true" className="flex items-center">
        <span
          className={cn('w-0.5 rounded-full', !paper && LIGHT_TOKENS.statRuleBg)}
          style={{ height: '60%', ...(paper ? { backgroundColor: PAPER_INK.primary.hex } : {}) }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span
          data-slot="big-stat-value"
          className={cn(
            BIG_STAT_VALUE_CLASS,
            paper ? PAPER_INK.primary.text : LIGHT_TOKENS.ink.primary.text
          )}
        >
          {reduced ? (
            formatBigStatValue(value, decimalPlaces, suffix, locale)
          ) : (
            <>
              {/* Scope note (residual, disclosed — not silently left):
                  `NumberTicker` (`@/components/ui/number-ticker.tsx`) still
                  formats its live count-up with a hardcoded
                  `Intl.NumberFormat('en-US', ...)` — it is a SHARED,
                  non-marketing primitive (also consumed by
                  `dashboard/insights/InsightsSummaryCards.tsx`), not
                  P3/`ui-light/**`-owned, so it is out of this fix's scope
                  (`rules/architecture.md`: never modify `components/ui/`
                  primitives from a feature lane). The static/reduced-motion
                  branch above (and the `sr-only` announcement in
                  `SocialProofSection.tsx`) are locale-correct; a `ro`-locale
                  visitor with motion enabled still sees en-US grouping
                  DURING the ticker's count-up animation, settling on an
                  en-US-grouped final value. Flagged for a follow-up
                  `NumberTicker` amendment (an additive, opt-in
                  `formatValue` prop) rather than forked/edited here. */}
              <NumberTicker
                value={value}
                decimalPlaces={decimalPlaces}
                startSignal={startSignal}
                className={cn(
                  paper ? PAPER_INK.primary.text : LIGHT_TOKENS.ink.primary.text,
                  'dark:text-[#15171C]',
                  'tracking-[-0.02em] [font-variant-numeric:lining-nums_tabular-nums_slashed-zero]'
                )}
              />
              {suffix}
            </>
          )}
        </span>
        <span
          data-slot="big-stat-label"
          className={cn(
            // R6 mono voice law (judge §2.3): converted from font-mono to
            // Inter -- matches SocialProofSection.tsx's BIG_STAT_LABEL_CLASS
            // (its snapshot===null UnavailableStat twin) byte-for-byte, so
            // the real ticker label and the unavailable em-dash label never
            // visibly diverge. ZERO color change (typography-only).
            'text-[11px] leading-4 font-semibold uppercase tracking-[0.06em]',
            paper ? PAPER_INK.muted.text : LIGHT_TOKENS.ink.tertiary.text
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
